import React, { useState, useEffect } from 'react';
import { QuestionDefinition, LogicRule } from '../../../types/survey.types';
import RuleBuilder from './RuleBuilder';
import { getRuleDescription } from '../../../utils/logicEvaluator';
import { useToast } from '../../common/Toast';

interface LogicSettingsProps {
  question: QuestionDefinition;
  allQuestions: QuestionDefinition[]; // All questions in the survey for reference
  onChange: (question: QuestionDefinition) => void;
}

const LogicSettings: React.FC<LogicSettingsProps> = ({
  question,
  allQuestions,
  onChange,
}) => {
  const toast = useToast();
  const [showRuleBuilder, setShowRuleBuilder] = useState(false);
  const [editingRule, setEditingRule] = useState<LogicRule | undefined>(undefined);
  const [justAddedRule, setJustAddedRule] = useState(false);

  const rules = question.logicRules || [];

  // Show reminder to save when rule is added
  useEffect(() => {
    if (justAddedRule && rules.length > 0) {
      const timer = setTimeout(() => setJustAddedRule(false), 5000);
      return () => clearTimeout(timer);
    }
  }, [justAddedRule, rules.length]);

  // Get questions that come BEFORE this one (can only reference previous questions)
  const availableQuestions = allQuestions.filter(q => {
    const currentIndex = allQuestions.findIndex(aq => aq.id === question.id);
    const refIndex = allQuestions.findIndex(aq => aq.id === q.id);
    return refIndex < currentIndex && refIndex !== -1;
  });

  const handleAddRule = () => {
    setEditingRule(undefined);
    setShowRuleBuilder(true);
  };

  const handleEditRule = (rule: LogicRule) => {
    setEditingRule(rule);
    setShowRuleBuilder(true);
  };

  const handleSaveRule = (rule: LogicRule) => {
    const existingRuleIndex = rules.findIndex(r => r.id === rule.id);
    let updatedRules: LogicRule[];

    if (existingRuleIndex !== -1) {
      // Update existing rule
      updatedRules = rules.map(r => r.id === rule.id ? rule : r);
      toast.success('Logic rule updated!');
    } else {
      // Add new rule
      updatedRules = [...rules, rule];
      toast.success('Logic rule added!');
      setJustAddedRule(true);
    }

    onChange({
      ...question,
      logicRules: updatedRules,
    });

    setShowRuleBuilder(false);
    setEditingRule(undefined);
  };

  const handleDeleteRule = (ruleId: string) => {
    const updatedRules = rules.filter(r => r.id !== ruleId);
    onChange({
      ...question,
      logicRules: updatedRules,
    });
  };

  const handleToggleRule = (ruleId: string) => {
    const updatedRules = rules.map(r =>
      r.id === ruleId ? { ...r, enabled: !r.enabled } : r
    );
    onChange({
      ...question,
      logicRules: updatedRules,
    });
  };

  const handleCancelBuilder = () => {
    setShowRuleBuilder(false);
    setEditingRule(undefined);
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h3 className="text-lg font-bold text-gray-900 mb-2">Conditional Logic Rules</h3>
        <p className="text-sm text-gray-600">
          Control when this question appears based on answers to previous questions.
        </p>
      </div>

      {/* Save Reminder Banner */}
      {justAddedRule && (
        <div className="bg-gradient-to-r from-green-50 to-blue-50 border-2 border-green-400 rounded-xl p-4 animate-pulse">
          <div className="flex items-center gap-3">
            <div className="flex-shrink-0 w-10 h-10 bg-green-500 rounded-full flex items-center justify-center">
              <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
              </svg>
            </div>
            <div className="flex-1">
              <p className="text-sm font-bold text-gray-900">Rule added successfully!</p>
              <p className="text-xs text-gray-700 mt-0.5">
                Don't forget to click <strong>"Save Changes"</strong> at the bottom of this modal to save the question with this logic rule.
              </p>
            </div>
            <button
              onClick={() => setJustAddedRule(false)}
              className="flex-shrink-0 text-gray-400 hover:text-gray-600"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
        </div>
      )}

      {/* No Rules State */}
      {rules.length === 0 && (
        <div className="bg-gradient-to-br from-blue-50 to-blue-100 border-2 border-blue-300 rounded-xl p-8 text-center">
          <div className="w-16 h-16 bg-white rounded-full flex items-center justify-center mx-auto mb-4 shadow-md">
            <svg className="w-8 h-8 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
            </svg>
          </div>
          <h4 className="text-lg font-bold text-gray-900 mb-2">No Logic Rules Yet</h4>
          <p className="text-gray-700 mb-6 max-w-md mx-auto">
            Add conditional logic to make this question appear or hide based on previous answers.
          </p>
          {availableQuestions.length === 0 ? (
            <div className="bg-yellow-50 border border-yellow-300 rounded-lg p-4 max-w-md mx-auto">
              <p className="text-sm text-yellow-800">
                <strong>Note:</strong> This is the first question, so there are no previous questions to reference.
                Add more questions before this one to enable conditional logic.
              </p>
            </div>
          ) : (
            <button
              onClick={handleAddRule}
              className="px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-all font-semibold shadow-md hover:shadow-lg"
            >
              + Create Your First Rule
            </button>
          )}
        </div>
      )}

      {/* Rules List */}
      {rules.length > 0 && (
        <div className="space-y-3">
          <div className="flex items-center justify-between mb-2">
            <h4 className="text-sm font-bold text-gray-700">
              {rules.length} Rule{rules.length !== 1 ? 's' : ''} Configured
            </h4>
            <button
              onClick={handleAddRule}
              className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-all text-sm font-semibold"
            >
              + Add Rule
            </button>
          </div>

          {rules.map((rule) => (
            <div
              key={rule.id}
              className={`bg-white border-2 rounded-xl p-4 transition-all ${
                rule.enabled
                  ? 'border-blue-300 shadow-sm'
                  : 'border-gray-300 opacity-60'
              }`}
            >
              <div className="flex items-start justify-between mb-2">
                <div className="flex-1">
                  <div className="flex items-center gap-2 mb-1">
                    <h5 className="font-bold text-gray-900">{rule.name}</h5>
                    {!rule.enabled && (
                      <span className="text-xs bg-gray-200 text-gray-600 px-2 py-0.5 rounded-full">
                        Disabled
                      </span>
                    )}
                  </div>
                  <p className="text-sm text-gray-700 italic leading-relaxed">
                    {getRuleDescription(rule, allQuestions)}
                  </p>
                </div>
              </div>

              <div className="flex items-center gap-2 mt-3 pt-3 border-t border-gray-200">
                <button
                  onClick={() => handleToggleRule(rule.id)}
                  className={`px-3 py-1.5 rounded-lg text-xs font-semibold transition-all ${
                    rule.enabled
                      ? 'bg-yellow-100 text-yellow-700 hover:bg-yellow-200'
                      : 'bg-green-100 text-green-700 hover:bg-green-200'
                  }`}
                >
                  {rule.enabled ? 'Disable' : 'Enable'}
                </button>
                <button
                  onClick={() => handleEditRule(rule)}
                  className="px-3 py-1.5 bg-blue-100 text-blue-700 rounded-lg hover:bg-blue-200 text-xs font-semibold transition-all"
                >
                  Edit
                </button>
                <button
                  onClick={() => {
                    if (window.confirm(`Delete rule "${rule.name}"?`)) {
                      handleDeleteRule(rule.id);
                    }
                  }}
                  className="px-3 py-1.5 bg-red-100 text-red-700 rounded-lg hover:bg-red-200 text-xs font-semibold transition-all"
                >
                  Delete
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Rule Builder Modal */}
      {showRuleBuilder && (
        <RuleBuilder
          rule={editingRule}
          currentQuestion={question}
          availableQuestions={availableQuestions}
          allQuestions={allQuestions}
          onSave={handleSaveRule}
          onCancel={handleCancelBuilder}
        />
      )}
    </div>
  );
};

export default LogicSettings;
